/**
 * The final painted-image pass.
 *
 * Three renders the scene normally into `beauty`, then renders normal and depth
 * into a second target so this pass can find drawn edges without asking every
 * material in the game to know about ink. The pass also owns the colour arc and
 * the screen-space paper that make the renderer read as an illustration rather
 * than as a clean Three scene.
 *
 * Two details are particularly easy to break here. Coplanar floors look like
 * depth discontinuities when viewed almost edge-on, so the depth threshold has
 * to grow with distance and relax at grazing angles. And this shader performs
 * the final sRGB encoding itself: letting Three encode the result a second time
 * washes out every zone. Paper is deliberately measured in screen pixels, not
 * world space; it is the page sitting in front of the camera, not dirt painted
 * onto Rosalie or the set.
 */

import * as THREE from 'three';
import { OUTLINE, PAPER, type ZonePalette } from '@/render/palette';

// Ink stays translucent enough to leave the painted value band readable.
const OUTLINE_OPACITY = 0.85;

// The maximum on-screen lean. The actual lean changes with the camera below.
const RAIN_WIND_ANGLE = 0.22;

/**
 * The storm's world-space direction, in world-Y radians.
 *
 * -0.70 sends it diagonally across the loading yard rather than along its square
 * fence lines. That is the useful read here: looking with or against the storm
 * leaves the marks nearly vertical, while a look across the yard exposes the
 * full lean and a half-turn reverses it. This is a direction in the WORLD, not
 * a screen-space styling angle.
 */
export const RAIN_WIND_AZIMUTH = -0.70;

/**
 * Camera-space travel through the procedural rain field, per world radian.
 *
 * A half-turn moves the across axis by 0.25, or about 48 of the 190 columns:
 * enough to reseed the marks after looking back without making ordinary camera
 * motion strobe. Pitch is gentler — a 45 degree look moves 0.094 of the fall
 * axis, about two thirds of a seven-row cell — so looking up or down reveals a
 * new part of the field without making the falling cadence jump.
 */
const RAIN_YAW_PARALLAX = 0.08;
const RAIN_PITCH_PARALLAX = 0.12;

/**
 * How far the unwrapped yaw may run before it is folded back, in radians.
 *
 * The pan needs a yaw that does NOT wrap at the atan2 seam, so it is
 * accumulated — and an accumulator with no bound is a slow float32 leak. The
 * uniform is a 32-bit float, and what it has to resolve is one rain column,
 * 1/RAIN_COLUMNS = 5.3e-3 of the across axis. At this bound the pan reaches
 * 4096 x 0.08 = 328, where the float32 ULP is about 3e-5 — some 175 times finer
 * than a column. Ten times this bound would still resolve; a session left
 * spinning overnight would not.
 *
 * 4096 rad is 652 full turns, or about 17 minutes of UNBROKEN maximum-rate yaw
 * in one direction (`ROSALIE_CAMERA.yawRate` is 4.0 rad/s). Folding costs one
 * frame in which the columns re-seed, which is the same thing an ordinary
 * quarter-turn does, so there is nothing to see even if a player gets there.
 *
 * `rain-direction.spec.ts` walks `wrapRainYaw` through ten thousand turns and
 * asserts both halves: that it stays inside the bound, and that every step it
 * takes is the true shortest delta. Without the second half a wrap that simply
 * froze the yaw would pass.
 */
const RAIN_YAW_WRAP = 4096;

/**
 * One step of the unwrapped-yaw accumulator: add the shortest signed change,
 * then fold back into (-RAIN_YAW_WRAP, RAIN_YAW_WRAP].
 *
 * Exported only so the gate can walk it without rendering ten thousand frames.
 */
export function wrapRainYaw(accumulated: number, previous: number, sampled: number): number {
  const delta = Math.atan2(Math.sin(sampled - previous), Math.cos(sampled - previous));
  const advanced = accumulated + delta;
  const span = RAIN_YAW_WRAP * 2;
  return advanced - span * Math.round(advanced / span);
}

/**
 * How much exposure a full-strength strike adds, on top of the image's own.
 *
 * At 1.0 the frame merely doubles, which measured as "brighter" and read as a
 * cloud moving off the moon. `docs/beat-6-plan.md` wants the opposite — *"it
 * should feel like the screen being hit"* — and the flash is the only pure white
 * in the game. At 3.0 the lit half of the frame clips out and the shadows stay
 * dark, which is what an exposure lift does and what a photograph of lightning
 * looks like.
 *
 * **This is one of three places the same `storm.flash` lands**, and it is the
 * cheapest of them: the light rig really raises the irradiance so surfaces
 * climb the toon ramp, and the stealth light grid takes the same number so what
 * the player sees and what the watcher sees cannot disagree (beat 6 rule 1).
 * Turning this up is not a substitute for the other two.
 */
const FLASH_EXPOSURE_GAIN = 3.0;

/**
 * Streak columns across the aspect-corrected frame, and cells down it.
 *
 * These two numbers are the shape of a raindrop and they are not independent of
 * each other: columns set the mark's WIDTH and rows set the cell it falls
 * through, so a streak is only a streak while the cell is several times taller
 * than the column is wide. At 1920x1080 this is roughly a 5 px column in a
 * 110 px cell, with a sixth of that cell lit.
 */
// Interpolated with toFixed(1): JS prints 190.0 as "190", and GLSL ES will not
// implicitly convert an int literal to a float. The whole shader fails to
// compile and the frame comes back BLACK, with the reason only in the console.
const RAIN_COLUMNS = 190;
const RAIN_ROWS = 7;

function clampUnit(value: number): number {
  return THREE.MathUtils.clamp(Number.isFinite(value) ? value : 0, 0, 1);
}

function clampRainDelta(seconds: number): number {
  return THREE.MathUtils.clamp(
    Number.isFinite(seconds) ? seconds : 0,
    0,
    Number.MAX_SAFE_INTEGER,
  );
}

export interface PostStackOptions {
  width: number;
  height: number;
  pixelRatio: number;
  zone: ZonePalette;
}

/**
 * The direction a zone's accent points in chroma space, as a unit vector.
 *
 * Not the accent colour: the grade's clamp is about *how saturated* a fragment
 * is allowed to be, and the only thing it needs from the accent is which way its
 * hue lies. Returning the axis rather than the colour is also what keeps the
 * exemption from being a tint — nothing in the shader ever adds `uAccentDir` to
 * a pixel, it only measures against it.
 *
 * A zone with no accent, or one whose accent is a near-neutral (the loading
 * yard's lightning white), gets a zero vector, and every dot product against it
 * is zero — which is exactly the no-op the default `accentCeiling` wants.
 */
function accentDirection(zone: ZonePalette): THREE.Vector3 {
  const accent = zone.accent;
  if (accent === undefined) return new THREE.Vector3(0, 0, 0);
  const colour = new THREE.Color(accent);
  const luma = colour.r * 0.2126 + colour.g * 0.7152 + colour.b * 0.0722;
  const direction = new THREE.Vector3(colour.r - luma, colour.g - luma, colour.b - luma);
  return direction.lengthSq() < 1e-6 ? new THREE.Vector3(0, 0, 0) : direction.normalize();
}

export interface PostStack {
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  setSize(width: number, height: number, pixelRatio: number): void;
  setZone(zone: ZonePalette): void;
  setChromaCeiling(ceiling: number): void;
  /**
   * The ceiling the SHADER is using, read back off the uniform. See the
   * implementation's note — it exists so a gate can measure rather than agree.
   */
  readonly chromaCeiling: number;
  setPaperSeed(seed: number): void;
  setRain(density: number): void;
  /** Read back the live uniforms so gameplay wiring is testable without guessing from pixels. */
  readonly rainTime: number;
  readonly rainDensity: number;
  advanceRain(seconds: number): void;
  setStormFlash(flash: number): void;
  dispose(): void;
}

class PostStackImpl implements PostStack {
  private readonly beauty = new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.HalfFloatType,
  });

  private readonly normalDepth = new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.UnsignedByteType,
  });

  private readonly normalMaterial = new THREE.MeshNormalMaterial();
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.Camera();
  private readonly resolution = new THREE.Vector2(1, 1);
  private readonly shadow = new THREE.Color();
  private readonly widthRange = new THREE.Vector2(
    OUTLINE.widthRange[0],
    OUTLINE.widthRange[1],
  );
  private readonly material: THREE.RawShaderMaterial;
  private readonly quad: THREE.Mesh<THREE.BufferGeometry, THREE.RawShaderMaterial>;
  private readonly originalOutputColorSpace: string;
  private readonly rainForward = new THREE.Vector3();
  private chromaOverride: number | undefined;
  private previousRainYaw: number | undefined;
  private unwrappedRainYaw = 0;

  public constructor(
    private readonly renderer: THREE.WebGLRenderer,
    options: PostStackOptions,
  ) {
    this.normalDepth.depthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType);
    this.originalOutputColorSpace = renderer.outputColorSpace;

    // The shader encodes its final colour. r185 must not encode that result again.
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;

    this.shadow.setHex(options.zone.shadow);
    this.material = new THREE.RawShaderMaterial({
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uBeauty: { value: this.beauty.texture },
        uNormal: { value: this.normalDepth.texture },
        uDepth: { value: this.normalDepth.depthTexture },
        uResolution: { value: this.resolution },
        uShadow: { value: this.shadow },
        uCeiling: { value: options.zone.chromaCeiling },
        uAccentDir: { value: accentDirection(options.zone) },
        uAccentCeiling: { value: options.zone.accentCeiling ?? options.zone.chromaCeiling },
        uAccentColour: { value: new THREE.Color(options.zone.accent ?? 0xffffff) },
        uPaperSeed: { value: 17 },
        uRain: { value: 0 },
        uRainTime: { value: 0 },
        uRainLean: { value: 0 },
        uRainYawPan: { value: 0 },
        uRainPitchPan: { value: 0 },
        uFlash: { value: 0 },
        uOutlineWidth: { value: this.widthRange },
        uNear: { value: 0.01 },
        uFar: { value: 100 },
        uGrainOpacity: { value: PAPER.grainOpacity },
        uWeaveOpacity: { value: PAPER.weaveOpacity },
        uOutlineDarken: { value: OUTLINE.shadowDarkenFactor },
        uOutlineOpacity: { value: OUTLINE_OPACITY },
      },
      vertexShader: `
        precision highp float;

        attribute vec3 position;

        varying vec2 vUv;

        void main() {
          vUv = position.xy * 0.5 + 0.5;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;

        uniform sampler2D uBeauty;
        uniform sampler2D uNormal;
        uniform sampler2D uDepth;
        uniform vec2 uResolution;
        uniform vec2 uOutlineWidth;
        uniform vec3 uShadow;
        uniform float uCeiling;
        uniform vec3 uAccentDir;
        uniform float uAccentCeiling;
        uniform vec3 uAccentColour;
        uniform float uPaperSeed;
        uniform float uRain;
        uniform float uRainTime;
        uniform float uRainLean;
        uniform float uRainYawPan;
        uniform float uRainPitchPan;
        uniform float uFlash;
        uniform float uNear;
        uniform float uFar;
        uniform float uGrainOpacity;
        uniform float uWeaveOpacity;
        uniform float uOutlineDarken;
        uniform float uOutlineOpacity;

        varying vec2 vUv;

        const float FLASH_EXPOSURE_GAIN = ${FLASH_EXPOSURE_GAIN.toFixed(1)};
        const float RAIN_COLUMNS = ${RAIN_COLUMNS.toFixed(1)};
        const float RAIN_ROWS = ${RAIN_ROWS.toFixed(1)};

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7)) + uPaperSeed) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);

          return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x),
            f.y
          );
        }

        float linearDepth(float z) {
          float clip = z * 2.0 - 1.0;
          return (2.0 * uNear * uFar) / (uFar + uNear - clip * (uFar - uNear));
        }

        vec3 srgb(vec3 colour) {
          return mix(
            12.92 * colour,
            1.055 * pow(max(colour, 0.0), vec3(1.0 / 2.4)) - 0.055,
            step(vec3(0.0031308), colour)
          );
        }

        void main() {
          vec2 pixel = 1.0 / uResolution;
          // LOW frequency, deliberately. The first version varied on a ~8 px
          // period, which turns a single silhouette edge on and off along its
          // own length and reads as a dashed line — a stitch, not a brush
          // stroke. docs/art-direction.md asks for the weight to vary the way it
          // does on plate 4: heavy on a trailer body, thinning to nothing on
          // fence wire, over tens of pixels. 0.02 is a ~50 px period, 0.006 a
          // ~170 px one.
          float variation = mix(
            uOutlineWidth.x,
            uOutlineWidth.y,
            noise(vUv * uResolution * 0.02) * 0.55 +
              noise(vUv * uResolution * 0.006 + 19.0) * 0.45
          );
          vec2 offset = variation * pixel;

          float depth = linearDepth(texture2D(uDepth, vUv).x);
          float depthX = abs(
            depth - linearDepth(texture2D(uDepth, vUv + vec2(offset.x, 0.0)).x)
          );
          float depthY = abs(
            depth - linearDepth(texture2D(uDepth, vUv + vec2(0.0, offset.y)).x)
          );

          vec3 normal = normalize(texture2D(uNormal, vUv).rgb * 2.0 - 1.0);
          vec3 normalX = normalize(
            texture2D(uNormal, vUv + vec2(offset.x, 0.0)).rgb * 2.0 - 1.0
          );
          vec3 normalY = normalize(
            texture2D(uNormal, vUv + vec2(0.0, offset.y)).rgb * 2.0 - 1.0
          );
          float normalEdge = max(1.0 - dot(normal, normalX), 1.0 - dot(normal, normalY));
          float graze = 1.0 - abs(normal.z);

          // Coplanar floors otherwise become outlines at distance when seen edge-on.
          //
          // Loosened from (0.0015 + 0.0025 * depth) * (1 + graze * 4): a floor
          // at ten metres seen at a shallow angle can change several centimetres
          // of depth per pixel, which sat right on the old threshold and broke
          // into speckle along the walkway. Speckle is worse than a missing
          // line — a missed edge reads as a soft shape, stray dots read as dirt.
          float depthThreshold = (0.004 + 0.010 * depth) * (1.0 + graze * 8.0);
          float depthEdge = smoothstep(depthThreshold, depthThreshold * 2.0, max(depthX, depthY));
          float outlineEdge = max(depthEdge, smoothstep(0.08, 0.22, normalEdge));

          vec3 colour = texture2D(uBeauty, vUv).rgb;
          colour = mix(colour, uShadow * uOutlineDarken, outlineEdge * uOutlineOpacity);

          // Square the fixed-step flash so its tell remains a tell, while the
          // strike still reads as exposure entering the scene instead of paint
          // laid over it. Multiplication preserves the image's local contrast;
          // the accent tints the light without adding a white screen-space veil.
          float flashExposure = uFlash * uFlash * FLASH_EXPOSURE_GAIN;
          colour *= vec3(1.0) + uAccentColour * flashExposure;

          float luma = dot(colour, vec3(0.2126, 0.7152, 0.0722));
          float maximum = max(max(colour.r, colour.g), colour.b);
          float minimum = min(min(colour.r, colour.g), colour.b);
          float saturation = (maximum - minimum) / max(maximum, 0.00001);

          // ── One hue is allowed through, and only one ────────────────────
          //
          // NO BACKTICKS IN THIS COMMENT. It lives inside a template literal,
          // and a backtick ends the shader mid-sentence: the error names a line
          // in the comment and says a semicolon was expected, which is true and
          // completely unhelpful. toon.ts records the same trap; this is the
          // second time it has been paid for.
          //
          // The ceiling drains a zone toward grey and the cull corridor's is
          // 0.08, the lowest in the game. Spec 7 gives that same room "one red
          // lamp", and the first frames of beat 5 proved the two cannot both be
          // true under a flat clamp: a saturated red at a 0.08 ceiling is a
          // slightly warm white, and the beat's only colour was invisible.
          //
          // So the ceiling is per-hue. A fragment whose chroma points the same
          // way as the zone's authored accent is graded against uAccentCeiling
          // instead; everything else is unchanged, including every zone that
          // does not set one — accentCeiling defaults to chromaCeiling and this
          // whole branch collapses to what was here before. It is a ceiling per
          // DIRECTION, not a hue shift: nothing is ever pushed toward the
          // accent, and a grey pixel stays exactly as grey as it was.
          vec3 chroma = colour - vec3(luma);
          float chromaLength = length(chroma);
          float affinity = 0.0;
          if (uAccentCeiling > uCeiling && chromaLength > 0.0001) {
            // Cubed, so "nearly the accent's hue" is narrow. A soft falloff
            // would let the warm bounce off every surface the lamp touches
            // through as well, and the room would stop being monochrome by
            // degrees rather than in one place.
            affinity = pow(max(0.0, dot(chroma / chromaLength, uAccentDir)), 3.0);
          }
          float ceiling = mix(uCeiling, uAccentCeiling, affinity);

          // The clamp is a ceiling, not a grade-strength dial: below it, preserve paint colour.
          colour = mix(
            vec3(luma),
            colour,
            min(1.0, ceiling / max(saturation, 0.00001))
          );

          // Lift deep values toward the zone shadow so darkness remains painted, never neutral.
          colour = mix(colour, uShadow, smoothstep(0.12, 0.0, luma) * 0.5);

          // Fragment coordinates keep this fixed to the page; moving it with the camera reads as video noise.
          float grain = hash(floor(gl_FragCoord.xy) + uPaperSeed) - 0.5;
          colour *= 1.0 + grain * 2.0 * uGrainOpacity;

          vec2 paper = gl_FragCoord.xy + uPaperSeed;
          float weave = (
            sin(dot(paper, vec2(1.53, 0.35))) +
            sin(dot(paper, vec2(-0.35, 1.53)))
          ) * 0.5;
          colour *= 1.0 + weave * uWeaveOpacity;

          // Rain moves through the screen-space image while the paper above
          // stays pinned to fragment coordinates. Coupling them made the whole
          // page crawl with the weather instead of leaving falling marks on it.
          //
          // A streak has to be LONG and THIN, and the first build of this layer
          // was neither. It divided the frame into 110 columns by 28 rows,
          // which on a 16:9 frame is a cell about as wide as it is tall, so
          // every "streak" came out a block; and its column test was
          // step(1.0 - uRain, seed), which at full density lights EVERY column.
          // The result was a chunky checkerboard over the whole image — it read
          // as camouflage, not as weather. Three things fix it and all three
          // are needed: measure in pixels, make the cell tall, and let most
          // columns be empty.
          vec2 rainDirection = vec2(sin(uRainLean), -cos(uRainLean));
          vec2 rainAcross = vec2(-rainDirection.y, rainDirection.x);
          // Aspect-corrected, because a column measured in UV on a 16:9 frame is
          // nearly twice as wide as it is tall and the streak inherits that.
          vec2 rainUv = vec2(vUv.x * (uResolution.x / max(uResolution.y, 1.0)), vUv.y);
          // Pan BEFORE rainColumn is quantised. A camera turn must reseed the
          // hashed columns, not slide the same stencil over a different scene;
          // that difference is the parallax cue which says the weather is in
          // the world rather than painted on the lens.
          float rainAcrossAxis = dot(rainUv, rainAcross) + uRainYawPan;
          float rainFallAxis = dot(rainUv, rainDirection) + uRainPitchPan;

          float rainColumn = floor(rainAcrossAxis * RAIN_COLUMNS);
          float columnSeed = hash(vec2(rainColumn, 41.0));
          // At most a bit over half the columns carry a streak at full density.
          // Rain you can see through is rain; rain in every column is a wall.
          float rainColumnVisible = step(1.0 - uRain * 0.55, columnSeed);
          // Soft across the column's width. A hard-edged column is a rectangle,
          // and rectangles are what made the first version read as blocks.
          float acrossWithin = abs(fract(rainAcrossAxis * RAIN_COLUMNS) - 0.5) * 2.0;
          float acrossSoft = smoothstep(0.95, 0.15, acrossWithin);

          float rainSpeed = mix(0.55, 1.25, columnSeed);
          // ── MINUS, and it shipped as a plus, and the rain fell UPWARDS ──────
          //
          // rainFallAxis grows downward (rainDirection.y is negative and vUv.y is
          // 0 at the bottom of the frame), so a streak lives wherever
          // fract(rainTravel) is small — that is, on the line
          // rainFallAxis = (phase - uRainTime * rainSpeed) / RAIN_ROWS. With a
          // PLUS, growing time drives that line toward a smaller fall axis, which
          // is up the screen. Every drop in beats 6 and 7 rose.
          //
          // Nothing caught it because rain-look.spec.ts contains no assertion at
          // all — it is a screenshotter, and a still frame of rain is
          // direction-free. D-100's point exactly: the gate tested that rain
          // exists. post.spec.ts now samples the layer at two times and asserts
          // the streaks moved DOWN.
          //
          // NOTE: no backticks in this block. It is inside a template literal
          // that builds the shader source, and a stray one truncates the GLSL.
          float rainTravel = rainFallAxis * RAIN_ROWS - uRainTime * rainSpeed;
          float rainRow = floor(rainTravel);
          float streakSeed = hash(vec2(rainColumn, rainRow));
          // The lit run is a fraction of a TALL cell, so the mark is several
          // times longer than it is wide. That ratio is the whole difference
          // between a streak and a dash.
          float streakLength = mix(0.16, 0.34, streakSeed);
          float within = fract(rainTravel);
          float streak =
            smoothstep(0.0, 0.06, within) * (1.0 - smoothstep(0.06, streakLength, within));

          // Densest at the top of the frame: that is where the eye reads the
          // sky, and it keeps the weather off her.
          float rainTopWeight = smoothstep(-0.15, 1.0, vUv.y);
          float rain = rainColumnVisible * acrossSoft * streak * rainTopWeight
            * mix(0.6, 1.0, streakSeed);

          // This follows the chroma clamp: rain catches light in front of the
          // painted scene, so the zone's surface saturation ceiling must not
          // grade it. The vignette below remains the page's final edge.
          colour += vec3(0.80, 0.88, 1.0) * (rain * uRain * 0.055);

          // INVERTED in the first version, and it cost the room its depth.
          //
          // It read 1.0 - smoothstep(...), which is 1 at the centre and 0 at
          // the corners — so it darkened the middle of the frame and left the
          // edges alone. In a corridor shot the middle of the frame is the
          // hazed far end, so the fog that carries the whole sense of the hall
          // going on was being pulled 12% toward the zone shadow, and the post
          // stack looked like it was *removing* atmosphere.
          float vignette = smoothstep(0.28, 0.92, length(vUv - 0.5) * 1.4142);
          colour = mix(colour, uShadow, vignette * 0.12);

          gl_FragColor = vec4(srgb(clamp(colour, 0.0, 1.0)), 1.0);
        }
      `,
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([
        -1, -1, 0,
        3, -1, 0,
        -1, 3, 0,
      ], 3),
    );

    // One oversized triangle has no diagonal seam or duplicated quad vertices to shade.
    this.quad = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.quad);

    this.setSize(options.width, options.height, options.pixelRatio);
  }

  public setSize(width: number, height: number, pixelRatio: number): void {
    const scaledWidth = Math.max(1, Math.ceil(width * pixelRatio));
    const scaledHeight = Math.max(1, Math.ceil(height * pixelRatio));

    this.beauty.setSize(scaledWidth, scaledHeight);
    this.normalDepth.setSize(scaledWidth, scaledHeight);
    this.resolution.set(scaledWidth, scaledHeight);
  }

  public setZone(zone: ZonePalette): void {
    // A zone change clears the explicit test/debug ceiling override.
    this.chromaOverride = undefined;
    this.shadow.setHex(zone.shadow);

    const ceiling = this.material.uniforms['uCeiling'];
    if (ceiling) ceiling.value = zone.chromaCeiling;
    const accentDir = this.material.uniforms['uAccentDir'];
    if (accentDir) (accentDir.value as THREE.Vector3).copy(accentDirection(zone));
    const accentCeiling = this.material.uniforms['uAccentCeiling'];
    if (accentCeiling) accentCeiling.value = zone.accentCeiling ?? zone.chromaCeiling;
    const accentColour = this.material.uniforms['uAccentColour'];
    if (accentColour) (accentColour.value as THREE.Color).setHex(zone.accent ?? 0xffffff);
  }

  /**
   * The ceiling the shader is actually using. **A read-back, for a gate.**
   *
   * `bloom.ts`'s census reported the value it had *computed* rather than the value
   * the uniform holds, so deleting `setChromaCeiling`'s call site left the bloom
   * gate green while the frame never changed saturation — which is precisely the
   * failure `docs/beat-7-plan.md` says is the worst available for that shot. A
   * census that reads its own arithmetic is not measuring anything.
   */
  public get chromaCeiling(): number {
    const uniform = this.material.uniforms['uCeiling'];
    return typeof uniform?.value === 'number' ? uniform.value : Number.NaN;
  }

  public setChromaCeiling(ceiling: number): void {
    this.chromaOverride = THREE.MathUtils.clamp(ceiling, 0, 1);

    const uniform = this.material.uniforms['uCeiling'];
    if (uniform) uniform.value = this.chromaOverride;
  }

  public setPaperSeed(seed: number): void {
    // This is a plate-level choice, not a per-frame jitter: animated grain is video noise.
    const uniform = this.material.uniforms['uPaperSeed'];
    if (uniform) uniform.value = seed;
  }

  public setRain(density: number): void {
    const uniform = this.material.uniforms['uRain'];
    if (uniform) uniform.value = clampUnit(density);
  }

  public get rainTime(): number {
    const uniform = this.material.uniforms['uRainTime'];
    return typeof uniform?.value === 'number' ? uniform.value : Number.NaN;
  }

  public get rainDensity(): number {
    const uniform = this.material.uniforms['uRain'];
    return typeof uniform?.value === 'number' ? uniform.value : Number.NaN;
  }

  public advanceRain(seconds: number): void {
    const uniform = this.material.uniforms['uRainTime'];
    if (uniform) uniform.value = (uniform.value as number) + clampRainDelta(seconds);
  }

  public setStormFlash(flash: number): void {
    const uniform = this.material.uniforms['uFlash'];
    if (uniform) uniform.value = clampUnit(flash);
  }

  public render(scene: THREE.Scene, camera: THREE.Camera): void {
    const perspective = camera as THREE.PerspectiveCamera;
    const near = this.material.uniforms['uNear'];
    const far = this.material.uniforms['uFar'];

    if (near) near.value = perspective.near;
    if (far) far.value = perspective.far;

    // This must come from the matrix that is about to draw, rather than a yaw
    // pushed in from main.ts: beats 6 and 7 can hand the lens to their directors,
    // and the gameplay camera may then describe a different frame. The camera's
    // local -Z is its actual view direction in Three's world convention.
    camera.updateMatrixWorld();
    this.rainForward.set(0, 0, -1).transformDirection(camera.matrixWorld);
    const sampledYaw = Math.atan2(this.rainForward.x, this.rainForward.z);
    const pitch = Math.asin(THREE.MathUtils.clamp(this.rainForward.y, -1, 1));

    // atan2 jumps from pi to -pi once per full turn. That seam is harmless for
    // the periodic sin() lean, but applying it directly to the field pan snapped
    // every column at once. Keep the pan's yaw continuous by adding only the
    // shortest signed change since the preceding rendered camera matrix — and
    // bounded, because an unbounded accumulator is a float32 leak. See
    // `wrapRainYaw`, which owns both halves of that.
    this.unwrappedRainYaw = this.previousRainYaw === undefined
      ? sampledYaw
      : wrapRainYaw(this.unwrappedRainYaw, this.previousRainYaw, sampledYaw);
    this.previousRainYaw = sampledYaw;

    const rainLean = RAIN_WIND_ANGLE * Math.sin(sampledYaw - RAIN_WIND_AZIMUTH);
    const rainLeanUniform = this.material.uniforms['uRainLean'];
    const rainYawPanUniform = this.material.uniforms['uRainYawPan'];
    const rainPitchPanUniform = this.material.uniforms['uRainPitchPan'];
    if (rainLeanUniform) rainLeanUniform.value = rainLean;
    if (rainYawPanUniform) rainYawPanUniform.value = this.unwrappedRainYaw * RAIN_YAW_PARALLAX;
    if (rainPitchPanUniform) rainPitchPanUniform.value = pitch * RAIN_PITCH_PARALLAX;

    // A caller may already be using an override; restore it even if this pass throws.
    const originalOverride = scene.overrideMaterial;
    try {
      scene.overrideMaterial = this.normalMaterial;
      this.renderer.setRenderTarget(this.normalDepth);
      this.renderer.clear();
      this.renderer.render(scene, camera);
    } finally {
      scene.overrideMaterial = originalOverride;
    }

    this.renderer.setRenderTarget(this.beauty);
    this.renderer.clear();
    this.renderer.render(scene, camera);

    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    this.beauty.dispose();
    this.normalDepth.dispose();
    this.normalMaterial.dispose();
    this.material.dispose();
    this.quad.geometry.dispose();
    this.renderer.outputColorSpace = this.originalOutputColorSpace;
  }
}

export function createPostStack(
  renderer: THREE.WebGLRenderer,
  options: PostStackOptions,
): PostStack {
  return new PostStackImpl(renderer, options);
}
